import { useEffect, useRef, type ReactNode } from "react";

import { CloseIcon } from "../icons";

/**
 * Native <dialog> gives the focus trap, Esc handling, and ::backdrop for free.
 * jsdom lacks showModal, so vitest.setup.ts installs a minimal polyfill.
 */
export function TableDialog({
  open,
  title,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  if (!open) return null;
  return (
    <dialog
      ref={ref}
      className="table-dialog"
      aria-label={title}
      onClose={onClose}
    >
      <header className="table-dialog__header">
        <h2>{title}</h2>
        <button
          className="icon-button"
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          <CloseIcon size={16} />
        </button>
      </header>
      <div className="table-dialog__body">{children}</div>
      {footer && <footer className="table-dialog__footer">{footer}</footer>}
    </dialog>
  );
}
